// src/components/forms/variants/embedded/types.ts

import type { 
  BaseFormProps, 
  ContentConfig, 
  ThemeConfig,
  FeatureListItemConfig,
  PrivacyConfig,
  BadgeConfig,
  SubtitleConfig
} from '~/components/forms/base/types'
import { siteConfig } from '~/lib/config/site'

/**
 * Layout presets for the embedded email capture form.
 * These presets define different visual styles and layouts that can be applied to the form.
 * 
 * @remarks
 * Each preset comes with its own set of default configurations for theme, content, and validation.
 * The presets are designed to work seamlessly with both light and dark modes.
 * 
 * @type {'banner' | 'card' | 'inline' | 'indigo' | 'minimal'}
 */
export type EmbeddedPresetLayout = 
  | 'banner' // Banner style with indigo background and gradient effect
  | 'card'   // Card style with elevated white/dark background
  | 'inline' // Minimal inline style without container styling
  | 'indigo' // Indigo-themed style with subtle background
  | 'minimal' // Bare bones, no container, no padding, just input and button

/**
 * Embedded-specific theme configuration
 */
export interface EmbeddedThemeConfig extends ThemeConfig {
  /** Primary accent color used for interactive elements */
  accentColor?: string
  /** Primary text color */
  textColor?: string
  /** Background color of the form container */
  backgroundColor?: string
}

/**
 * Props interface for the EmbeddedEmailCapture component.
 * Extends the base form props while allowing customization of theme and content.
 * 
 * @interface EmbeddedEmailCaptureProps
 * @extends {Omit<BaseFormProps, 'theme' | 'content'>}
 * 
 * @example
 * ```tsx
 * <EmbeddedEmailCapture
 *   preset="banner"
 *   theme={{
 *     accentColor: '#4F46E5',
 *     textColor: '#111827'
 *   }}
 *   content={{
 *     title: { text: 'Subscribe to our newsletter' },
 *     button: { text: 'Sign up' }
 *   }}
 * />
 * ```
 * @property {EmbeddedPresetLayout} [preset] - Layout preset to use for styling the form
 * @property {object} [theme] - Theme configuration that extends the base theme
 * @property {string} [theme.accentColor] - Primary accent color used for interactive elements
 * @property {string} [theme.textColor] - Primary text color
 * @property {string} [theme.backgroundColor] - Background color of the form container
 * @property {object} content - Content configuration that extends the base content
 */
export interface EmbeddedEmailCaptureProps extends Omit<BaseFormProps, 'theme' | 'content'> {
  /** 
   * Layout preset to use for styling the form.
   * @default 'banner'
   */
  preset?: EmbeddedPresetLayout

  /** 
   * Theme configuration that extends the base theme.
   * Allows customization of colors and visual properties.
   */
  theme?: EmbeddedThemeConfig

  /** 
   * Content configuration that extends the base content.
   * Must provide all required content elements.
   */
  content: ContentConfig & {
    /** Optional badge configuration */
    badge?: BadgeConfig
    /** Optional subtitle configuration */
    subtitle?: SubtitleConfig
    /** Optional feature list */
    features?: FeatureListItemConfig[]
    /** Optional privacy notice configuration */
    privacy?: PrivacyConfig
  }
}

/**
 * Default preset configurations for different layout styles.
 * Each preset provides a complete set of theme, content, and validation settings.
 * 
 * @remarks
 * - Banner: Full-width design with gradient background
 * - Card: Elevated container with clean borders
 * - Inline: Minimal design for embedding within content
 * - Indigo: Branded design with indigo color scheme
 * - Minimal: Bare bones, no container, no padding, just input and button
 * 
 * All presets include:
 * - Responsive design (mobile-first)
 * - Dark mode support
 * - Accessibility features
 * - Form validation
 * - Loading states
 * - Success/error messages
 * - Privacy policy links
 * 
 * @example
 * ```tsx
 * // Using a preset directly
 * const config = EMBEDDED_PRESETS.banner;
 * 
 * // Extending a preset
 * const customConfig = {
 *   ...EMBEDDED_PRESETS.banner,
 *   theme: {
 *     ...EMBEDDED_PRESETS.banner.theme,
 *     className: {
 *       container: 'custom-container-class'
 *     }
 *   }
 * };
 * ```
 * @type {Record<EmbeddedPresetLayout, Partial<EmbeddedEmailCaptureProps>>}
 */
export const EMBEDDED_PRESETS: Record<EmbeddedPresetLayout, Partial<EmbeddedEmailCaptureProps>> = {
  banner: {
    theme: {
      variant: 'embedded',
      layout: 'inline',
      size: 'md',
      theme: 'light',
      className: {
        container: 'w-full bg-gradient-to-tr from-indigo-50/90 via-indigo-50/50 to-white dark:from-indigo-950/30 dark:via-indigo-950/10 dark:to-gray-900 rounded-2xl shadow-sm border border-indigo-100/50 dark:border-indigo-500/10 backdrop-blur-sm'
      }
    },
    content: {
      input: {
        placeholder: 'Enter your email',
        className: 'w-full px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base rounded-xl bg-white/90 dark:bg-gray-900/60 border-0 ring-1 ring-indigo-100 dark:ring-indigo-500/20 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 placeholder:text-gray-400 dark:placeholder:text-gray-500'
      },
      button: {
        text: 'Subscribe',
        loadingText: 'Subscribing...',
        className: 'inline-flex items-center justify-center gap-1 px-4 md:px-6 py-2.5 md:py-3 text-sm md:text-base font-medium rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 ring-1 ring-indigo-600/10 dark:ring-indigo-500/20 shadow-sm transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 active:ring-0 active:ring-offset-0 active:translate-y-px'
      },
      messages: {
        success: 'Thanks for subscribing!',
        error: 'Something went wrong. Please try again.'
      },
      privacy: {
        enabled: true,
        text: 'By subscribing, you agree to our',
        privacyLink: {
          text: 'Privacy Policy',
          url: `${siteConfig.baseLinks.privacy}`
        },
        termsLink: {
          text: 'Terms of Service',
          url: `${siteConfig.baseLinks.terms}`
        },
        className: 'mt-3'
      }
    },
    validation: {
      rules: {
        required: true,
        format: true,
        minLength: 5,
        maxLength: 254,
        blockedDomains: ['temp.com', 'tempmail.com', 'disposable.com']
      },
      messages: {
        required: 'Please enter your email address',
        invalid: 'Please enter a valid email address',
        blocked: 'Please use a permanent email address'
      },
      timing: {
        validateOnChange: true,
        validateOnBlur: true,
        validateOnSubmit: true,
        debounceMs: 300
      }
    }
  },
  card: {
    theme: {
      variant: 'embedded',
      layout: 'stacked',
      size: 'md',
      theme: 'light',
      className: {
        container: 'w-full bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 overflow-hidden'
      }
    },
    content: {
      input: {
        placeholder: 'Enter your email',
        className: 'w-full px-4 py-3 text-base rounded-xl bg-gray-50 dark:bg-gray-800/80 border-0 ring-1 ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 placeholder:text-gray-400 dark:placeholder:text-gray-500'
      },
      button: {
        text: 'Subscribe',
        loadingText: 'Subscribing...',
        className: 'inline-flex items-center justify-center gap-1 px-6 py-3 text-base font-medium rounded-xl bg-gradient-to-b from-indigo-500 to-indigo-600 text-white hover:from-indigo-400 hover:to-indigo-500 ring-1 ring-indigo-600/10 dark:ring-indigo-500/20 shadow-sm transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 active:ring-0 active:ring-offset-0 active:translate-y-px'
      },
      messages: {
        success: 'Thanks for subscribing!',
        error: 'Something went wrong. Please try again.'
      },
      privacy: {
        enabled: true,
        text: 'By subscribing, you agree to our',
        privacyLink: {
          text: 'Privacy Policy',
          url: `${siteConfig.baseLinks.privacy}`
        },
        termsLink: {
          text: 'Terms of Service',
          url: `${siteConfig.baseLinks.terms}`
        },
        className: 'mt-3'
      }
    },
    validation: {
      rules: {
        required: true,
        format: true,
        minLength: 5,
        maxLength: 254,
        blockedDomains: ['temp.com', 'tempmail.com', 'disposable.com']
      },
      messages: {
        required: 'Please enter your email address',
        invalid: 'Please enter a valid email address',
        blocked: 'Please use a permanent email address'
      },
      timing: {
        validateOnChange: true,
        validateOnBlur: true,
        validateOnSubmit: true,
        debounceMs: 300
      }
    }
  },
  inline: {
    theme: {
      variant: 'embedded',
      layout: 'inline',
      size: 'sm',
      theme: 'light',
      className: {
        container: 'w-full bg-gradient-to-tr from-indigo-60/70 via-indigo-60/40 to-white dark:from-indigo-950/20 dark:via-indigo-950/5 dark:to-gray-900 rounded-2xl border border-indigo-100/50 dark:border-indigo-500/10'
      }
    },
    content: {
      input: {
        placeholder: 'Enter your email',
        className: 'w-full px-4 py-3 text-base rounded-xl bg-white/90 dark:bg-gray-900/60 border-0 ring-1 ring-indigo-100 dark:ring-indigo-500/20 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 placeholder:text-gray-400 dark:placeholder:text-gray-500'
      },
      button: {
        text: 'Subscribe',
        loadingText: 'Subscribing...',
        className: 'inline-flex items-center justify-center gap-1 px-5 py-3 text-base font-medium rounded-xl bg-indigo-600/90 text-white hover:bg-indigo-500 ring-1 ring-indigo-600/10 dark:ring-indigo-500/20 shadow-sm transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 active:ring-0 active:ring-offset-0 active:translate-y-px'
      },
      messages: {
        success: 'Thanks for subscribing!',
        error: 'Something went wrong. Please try again.'
      },
      privacy: {
        enabled: true,
        text: 'By subscribing, you agree to our',
        privacyLink: {
          text: 'Privacy Policy',
          url: `${siteConfig.baseLinks.privacy}`
        },
        termsLink: {
          text: 'Terms of Service',
          url: `${siteConfig.baseLinks.terms}`
        },
        className: 'mt-3'
      }
    },
    validation: {
      rules: {
        required: true,
        format: true,
        minLength: 5,
        maxLength: 254,
        blockedDomains: ['temp.com', 'tempmail.com', 'disposable.com']
      },
      messages: {
        required: 'Please enter your email address',
        invalid: 'Please enter a valid email address',
        blocked: 'Please use a permanent email address'
      },
      timing: {
        validateOnChange: true,
        validateOnBlur: true,
        validateOnSubmit: true,
        debounceMs: 300
      }
    }
  },
  indigo: {
    theme: {
      variant: 'embedded',
      layout: 'inline',
      size: 'sm',
      theme: 'light',
      className: {
        container: 'rounded-lg bg-indigo-50 p-6 dark:bg-indigo-900/20'
      }
    },
    content: {
      input: {
        placeholder: 'Enter your email',
        className: 'w-full min-w-0 rounded-md border-0 px-3 py-2 text-indigo-900 shadow-sm ring-1 ring-inset ring-indigo-300 placeholder:text-indigo-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:bg-indigo-950 dark:text-indigo-100 dark:ring-indigo-700 dark:placeholder:text-indigo-500 sm:text-sm sm:leading-6'
      },
      button: {
        text: 'Subscribe',
        loadingText: 'Subscribing...',
        icon: '<RiMailLine className="h-4 w-4" />',
        className: 'flex w-full items-center justify-center gap-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 active:ring-0 active:ring-offset-0 active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto'
      },
      messages: {
        success: 'Thanks for subscribing!',
        error: 'Something went wrong. Please try again.'
      },
      privacy: {
        enabled: true,
        text: 'By subscribing, you agree to our',
        privacyLink: {
          text: 'Privacy Policy',
          url: `${siteConfig.baseLinks.privacy}`
        },
        termsLink: {
          text: 'Terms of Service',
          url: `${siteConfig.baseLinks.terms}`
        },
        className: 'mt-3'
      }
    }
  },
  minimal: {
    theme: {
      variant: 'embedded',
      layout: 'inline',
      size: 'md',
      theme: 'light',
      className: {
        container: 'w-full',
        form: 'space-y-3'
      }
    },
    content: {
      input: {
        placeholder: 'Enter your email',
        className: 'w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 dark:bg-gray-900 dark:text-white dark:ring-gray-700 focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:focus:ring-indigo-400 sm:text-sm sm:leading-6'
      },
      button: {
        text: 'Subscribe',
        loadingText: 'Subscribing...',
        className: 'w-full sm:w-auto rounded-md bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:hover:bg-indigo-400'
      },
      messages: {
        success: 'Thanks for subscribing!',
        error: 'Please enter a valid email address.'
      },
      privacy: {
        enabled: true,
        text: 'By continuing, you agree to our',
        className: 'text-xs text-gray-500 dark:text-gray-400',
        privacyLink: {
          text: 'Privacy Policy',
          url: `${siteConfig.baseLinks.privacy}`
        },
        termsLink: {
          text: 'Terms of Service',
          url: `${siteConfig.baseLinks.terms}`
        }
      }
    },
    features: {
      ui: {
        showTitle: false,
        showDescription: false,
        showPrivacy: true,
        embedInInput: false
      }
    }
  }
} as const